import React from 'react';
import { formatCallDuration } from './usageTiers';

// 3-month call-timeline micro-chart for one account row in the admin
// Accounts tab (Usage view). Each multi-person call is a vertical bar
// positioned by start date; bar height scales with sqrt(duration) capped at
// CAP_HOURS so a 1-hour call is still visible next to a 4-hour one (half
// height = 1 hour). Weekly gridlines + month labels make weekly rhythms and
// drop-offs readable at a glance.
//
// Above the call plot, one thin strip per family shows the device's state
// over the same window (from usageAnalytics.statusIntervals): offline grey,
// asleep yellow, home orange, in-igloo green. Igloo slivers are widened to at
// least 1px so they stay visible at 3-month scale.
// Data-dense on purpose: no axes, no legend.

const CAP_HOURS = 4;
const MIN_BAR_FRACTION = 0.15; // even a 1-minute call renders a visible tick

const STRIP_HEIGHT = 4;
const STRIP_GAP = 1;
const STRIP_SECTION_GAP = 2; // between the strips block and the call plot

// Shared with the History view's daily strips so the two views never disagree
// about what a colour means.
export const STATUS_COLORS = {
  offline: '#9ca3af',
  asleep: '#facc15',
  home: '#fb923c',
  igloo: '#16a34a',
  // 'unknown' renders as the base-rect color (no segment drawn)
};

// The neutral base the strips are drawn on — "nothing known here".
export const STATUS_BASE_COLOR = '#f3f4f6';

function StatusStrip({ familyId, intervals, y, width, msToX }) {
  const segs = (intervals || []).filter(s => STATUS_COLORS[s.state]);
  // Draw igloo segments last so their (min-width-widened) slivers aren't
  // painted over by neighbouring long segments.
  const ordered = [...segs.filter(s => s.state !== 'igloo'), ...segs.filter(s => s.state === 'igloo')];
  return (
    <g>
      <rect x={0} y={y} width={width} height={STRIP_HEIGHT} rx={1} fill={STATUS_BASE_COLOR}>
        <title>{`${familyId} — device state (grey offline, yellow asleep, orange home, green in Higlo)`}</title>
      </rect>
      {ordered.map((s, i) => {
        const sx = Math.max(msToX(s.startMs), 0);
        const ex = Math.min(msToX(s.endMs), width);
        if (ex <= 0 || sx >= width || ex <= sx) return null;
        const minW = s.state === 'igloo' ? 1 : 0.5;
        const w = Math.max(ex - sx, minW);
        return (
          <rect
            key={i}
            x={Math.min(sx, width - w)}
            y={y}
            width={w}
            height={STRIP_HEIGHT}
            fill={STATUS_COLORS[s.state]}
          />
        );
      })}
    </g>
  );
}

export default function UsageTimeline({
  calls, // [{ startMs, durationSeconds, peakFamilies, iglooId }]
  familyStatuses = [], // [{ familyId, intervals: [{state, startMs, endMs}] }]
  windowDays = 91,
  width = 320,
  height = 36, // call plot + month labels; family strips add height on top
  nowMs = Date.now(),
}) {
  const windowStartMs = nowMs - windowDays * 24 * 3600 * 1000;
  const msToX = (ms) => ((ms - windowStartMs) / (nowMs - windowStartMs)) * width;

  const stripsHeight = familyStatuses.length > 0
    ? familyStatuses.length * (STRIP_HEIGHT + STRIP_GAP) + STRIP_SECTION_GAP
    : 0;
  const labelSpace = 9; // px at the bottom for month labels
  const plotTop = stripsHeight;
  const plotHeight = height - labelSpace;
  const plotBottom = plotTop + plotHeight;
  const totalHeight = stripsHeight + height;

  // Weekly gridlines on Mondays; month boundaries slightly stronger + labelled.
  const gridlines = [];
  const monthLabels = [];
  const cursor = new Date(windowStartMs);
  cursor.setHours(0, 0, 0, 0);
  // advance to the next Monday
  cursor.setDate(cursor.getDate() + ((8 - cursor.getDay()) % 7));
  while (cursor.getTime() <= nowMs) {
    gridlines.push({ x: msToX(cursor.getTime()), strong: false });
    cursor.setDate(cursor.getDate() + 7);
  }
  const monthCursor = new Date(windowStartMs);
  monthCursor.setHours(0, 0, 0, 0);
  monthCursor.setDate(1);
  monthCursor.setMonth(monthCursor.getMonth() + 1); // first boundary inside the window
  while (monthCursor.getTime() <= nowMs) {
    const x = msToX(monthCursor.getTime());
    gridlines.push({ x, strong: true });
    // Skip labels too close to the right edge — they'd render clipped.
    if (x <= width - 15) {
      monthLabels.push({
        x,
        text: monthCursor.toLocaleDateString(undefined, { month: 'short' }),
      });
    }
    monthCursor.setMonth(monthCursor.getMonth() + 1);
  }

  const visibleCalls = (calls || []).filter(c => c.startMs >= windowStartMs && c.startMs <= nowMs);

  return (
    <svg
      width={width}
      height={totalHeight}
      className="flex-shrink-0"
      role="img"
      aria-label={`Call timeline: ${visibleCalls.length} multi-person call${visibleCalls.length === 1 ? '' : 's'} in the last 3 months, ${familyStatuses.length} device status strip${familyStatuses.length === 1 ? '' : 's'}`}
    >
      {/* per-family device status strips */}
      {familyStatuses.map((fs, row) => (
        <StatusStrip
          key={fs.familyId || row}
          familyId={fs.familyId}
          intervals={fs.intervals}
          y={row * (STRIP_HEIGHT + STRIP_GAP)}
          width={width}
          msToX={msToX}
        />
      ))}
      {/* baseline */}
      <line x1={0} y1={plotBottom} x2={width} y2={plotBottom} stroke="#e5e7eb" strokeWidth={1} />
      {gridlines.map((g, i) => (
        <line
          key={i}
          x1={g.x}
          y1={plotTop}
          x2={g.x}
          y2={plotBottom}
          stroke={g.strong ? '#d1d5db' : '#f3f4f6'}
          strokeWidth={1}
        />
      ))}
      {monthLabels.map((m, i) => (
        <text key={i} x={m.x + 2} y={totalHeight - 1} fontSize={8} fill="#9ca3af">
          {m.text}
        </text>
      ))}
      {visibleCalls.length === 0 && (
        <text x={width / 2} y={plotTop + plotHeight / 2 + 3} fontSize={9} fill="#d1d5db" textAnchor="middle">
          no calls
        </text>
      )}
      {visibleCalls.map((c, i) => {
        const capSeconds = CAP_HOURS * 3600;
        const frac = Math.sqrt(Math.min(c.durationSeconds, capSeconds) / capSeconds);
        const barH = Math.max(frac, MIN_BAR_FRACTION) * plotHeight;
        const x = Math.min(Math.max(msToX(c.startMs), 0), width - 2.5);
        const date = new Date(c.startMs);
        return (
          <rect
            key={i}
            x={x}
            y={plotBottom - barH}
            width={2.5}
            height={barH}
            rx={0.5}
            fill="#16a34a"
          >
            <title>
              {`${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — ${formatCallDuration(c.durationSeconds)}, ${c.peakFamilies} families`}
            </title>
          </rect>
        );
      })}
    </svg>
  );
}
